// lib/slides/history.ts
//
// Undo/redo for a deck, as pure functions over a snapshot stack.
//
// SNAPSHOTS, NOT INVERSE OPERATIONS. The alternative - every action shipping its
// own "and here is how to undo me" - has to be written once per action and is
// wrong the day someone adds an action and forgets. There is exactly one place a
// deck's document is ever replaced (slidesStore's applyDocMutation), so keeping
// the previous document there covers every function that exists today and every
// one added later, including the agent's. A deck document is a page list with
// overlay elements and URL references, not pixels, so a bounded stack of them is
// cheap.
//
// COALESCING is the only subtlety. An opacity slider fires a mutation per step:
// without folding, one drag buries the real history under sixty entries and the
// user's previous edit falls off the end of the stack. Entries therefore carry a
// key, and a mutation that repeats the previous key within a short window
// replaces nothing and pushes nothing - the snapshot already on the stack is
// still the right thing to go back to.

/** How many steps back a deck can go. Deep enough to cover a working session,
 *  bounded so a long one cannot grow without limit. */
export const HISTORY_LIMIT = 40;

/** How long a repeat of the same key still counts as the same gesture. Long
 *  enough for a slider drag or an agent command's burst of placements, short
 *  enough that deliberately doing the same thing twice stays two steps. */
export const COALESCE_WINDOW_MS = 1200;

export type HistoryEntry<Doc> = { doc: Doc; key: string | null; at: number };

export type DeckHistory<Doc> = { past: HistoryEntry<Doc>[]; future: HistoryEntry<Doc>[] };

export const emptyHistory = <Doc,>(): DeckHistory<Doc> => ({ past: [], future: [] });

/**
 * Record `doc` (the state BEFORE a mutation) on the undo stack.
 *
 * Returns the history unchanged when the mutation continues the gesture already
 * on top of the stack. Any new step drops the redo stack: once you edit after
 * undoing, the branch you abandoned is not reachable any more, and offering a
 * redo that would splice it back in is how editors corrupt documents.
 */
export function pushHistory<Doc>(
  history: DeckHistory<Doc>,
  doc: Doc,
  key: string | null,
  now: number
): DeckHistory<Doc> {
  const top = history.past[history.past.length - 1];
  if (key !== null && top && top.key === key && now - top.at <= COALESCE_WINDOW_MS) {
    // Same gesture: keep the older snapshot (undo should land before the whole
    // drag, not one slider tick into it) but move the clock forward, so a
    // continuous drag stays one entry however long it lasts.
    const past = history.past.slice(0, -1).concat({ ...top, at: now });
    return { past, future: [] };
  }
  const past = history.past.concat({ doc, key, at: now });
  return { past: past.slice(-HISTORY_LIMIT), future: [] };
}

/**
 * Step back one entry. `current` is the live document, which goes onto the redo
 * stack. Returns null when there is nothing to undo, so the caller can leave the
 * store untouched rather than writing an identical state and triggering a save.
 */
export function undoHistory<Doc>(
  history: DeckHistory<Doc>,
  current: Doc
): { doc: Doc; history: DeckHistory<Doc> } | null {
  const entry = history.past[history.past.length - 1];
  if (!entry) return null;
  return {
    doc: entry.doc,
    history: {
      past: history.past.slice(0, -1),
      // The redo entry carries no key: replaying it must never fold into a
      // neighbouring gesture.
      future: history.future.concat({ doc: current, key: null, at: entry.at }),
    },
  };
}

/** Step forward one entry. Mirror of undoHistory. */
export function redoHistory<Doc>(
  history: DeckHistory<Doc>,
  current: Doc
): { doc: Doc; history: DeckHistory<Doc> } | null {
  const entry = history.future[history.future.length - 1];
  if (!entry) return null;
  return {
    doc: entry.doc,
    history: {
      past: history.past.concat({ doc: current, key: null, at: entry.at }),
      future: history.future.slice(0, -1),
    },
  };
}
